import _ from 'lodash';

import { ToolConfirmationStatus } from 'constants/aiChat';

const EMPTY_LIST = [];

const getListForMessage = (state, messageId) => state?.[messageId] ?? EMPTY_LIST;

const findIndexByToolCallId = (list, toolCallId) =>
  list.findIndex(confirmation => confirmation.toolCallId === toolCallId);

const replaceAt = (list, index, next) => {
  const copy = [...list];
  copy[index] = next;

  return copy;
};

export const appendPendingConfirmation = (state, payload) => {
  if (_.isNil(payload?.messageId) || _.isNil(payload?.toolCallId)) {
    return state;
  }

  const list = getListForMessage(state, payload.messageId);
  const existingIndex = findIndexByToolCallId(list, payload.toolCallId);

  const nextEntry = {
    toolCallId: payload.toolCallId,
    toolName: payload.toolName ?? null,
    friendlyName: payload.friendlyName ?? null,
    toolArguments: payload.toolArguments ?? null,
    policy: payload.policy ?? null,
    status: ToolConfirmationStatus.PENDING,
  };

  if (existingIndex === -1) {
    return {
      ...state,
      [payload.messageId]: [...list, nextEntry],
    };
  }

  return {
    ...state,
    [payload.messageId]: replaceAt(list, existingIndex, {
      ...list[existingIndex],
      ...nextEntry,
    }),
  };
};

export const updateConfirmationStatus = (state, { messageId, toolCallId, changes }) => {
  if (_.isNil(messageId) || _.isNil(toolCallId)) {
    return state;
  }

  const list = getListForMessage(state, messageId);
  const index = findIndexByToolCallId(list, toolCallId);

  if (index === -1) {
    return state;
  }

  return {
    ...state,
    [messageId]: replaceAt(list, index, {
      ...list[index],
      ...changes,
    }),
  };
};

export const mergePendingConfirmationsForMessage = (state, { messageId, confirmations }) => {
  if (_.isNil(messageId)) {
    return state;
  }

  const existingList = getListForMessage(state, messageId);
  const byId = new Map(existingList.map(confirmation => [confirmation.toolCallId, confirmation]));

  (confirmations ?? []).forEach(incoming => {
    if (_.isNil(incoming?.toolCallId)) {
      return;
    }

    const existing = byId.get(incoming.toolCallId);

    byId.set(incoming.toolCallId, {
      toolCallId: incoming.toolCallId,
      toolName: incoming.toolName ?? existing?.toolName ?? null,
      friendlyName: incoming.friendlyName ?? existing?.friendlyName ?? null,
      toolArguments: incoming.toolArguments ?? existing?.toolArguments ?? null,
      policy: incoming.policy ?? existing?.policy ?? null,
      status: existing?.status ?? ToolConfirmationStatus.PENDING,
      ...(existing?.result ? { result: existing.result } : {}),
      ...(existing?.error ? { error: existing.error } : {}),
    });
  });

  const nextList = Array.from(byId.values());

  if (_.isEqual(nextList, existingList)) {
    return state;
  }

  return {
    ...state,
    [messageId]: nextList,
  };
};
